/** 提交前去掉前端展示用的 @ 标记，避免污染实际提示词。支持 @图N、@视频N、@音频N */
export const stripPromptMentionsForSubmit = (text: string) => {
  return text
    .replace(/@图(\d+)/g, '图$1')
    .replace(/@视频(\d+)/g, '视频$1')
    .replace(/@音频(\d+)/g, '音频$1');
};

export interface NamedPromptMaterialMatch {
  nodeId: string;
  name: string;
}

export interface ActiveNamedMaterialsResult {
  text: string;
  matched: NamedPromptMaterialMatch[];
}

const promptMaterialNameKey = (name: string): string => name.trim().toLocaleLowerCase();

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isPromptMaterialNode = (node: { type: string }): boolean =>
  node.type === 'image' || node.type === 'video' || node.type === 'audio';

const hasMaterialReference = (node: {
  sourceAssetId?: string;
  url?: string;
}): boolean => Boolean(node.sourceAssetId?.trim() || node.url?.trim());

type PromptMaterialNode = {
  id: string;
  type: string;
  name: string;
  connected: boolean;
  sourceAssetId?: string;
  url?: string;
  thumb?: string;
};

type NamedPromptCandidate = {
  start: number;
  end: number;
  node: PromptMaterialNode;
  name: string;
};

/**
 * 按需在已激活素材中识别 prompt 名称，并转换为 `@素材名`。
 * 名称按最短优先匹配；已带 @ 的同名文本不会重复处理。
 */
export function prefixActiveNamedMaterials(
  text: string,
  nodes: PromptMaterialNode[],
): ActiveNamedMaterialsResult {
  if (!text || !nodes.length) {
    return { text, matched: [] };
  }

  const usableNodes = nodes.filter(
    (node) =>
      node.connected && isPromptMaterialNode(node) && node.name.trim() && hasMaterialReference(node),
  );
  const byName = new Map<string, typeof usableNodes>();
  for (const node of usableNodes) {
    const key = promptMaterialNameKey(node.name);
    const group = byName.get(key) || [];
    group.push(node);
    byName.set(key, group);
  }

  const namedMaterials = Array.from(byName.values()).map((candidates) => ({
    node: candidates[0],
    name: candidates[0].name.trim(),
  }));
  if (!namedMaterials.length) {
    return {
      text,
      matched: [],
    };
  }

  const protectedRanges: Array<{ start: number; end: number }> = [];
  for (const material of namedMaterials) {
    const mentionRe = new RegExp(`@${escapeRegExp(material.name)}`, 'giu');
    let mentionMatch: RegExpExecArray | null;
    while ((mentionMatch = mentionRe.exec(text)) !== null) {
      protectedRanges.push({
        start: mentionMatch.index,
        end: mentionMatch.index + mentionMatch[0].length,
      });
    }
  }

  const candidates: NamedPromptCandidate[] = [];
  for (const material of namedMaterials) {
    const nameRe = new RegExp(escapeRegExp(material.name), 'giu');
    let nameMatch: RegExpExecArray | null;
    while ((nameMatch = nameRe.exec(text)) !== null) {
      const start = nameMatch.index;
      const end = start + nameMatch[0].length;
      if (protectedRanges.some((range) => range.start <= start && end <= range.end)) continue;
      candidates.push({ start, end, node: material.node, name: material.name });
    }
  }

  candidates.sort(
    (a, b) => a.name.length - b.name.length || a.start - b.start || a.end - b.end,
  );
  const selected: NamedPromptCandidate[] = [];
  for (const candidate of candidates) {
    if (selected.some((item) => candidate.start < item.end && candidate.end > item.start)) continue;
    selected.push(candidate);
  }
  selected.sort((a, b) => a.start - b.start);

  let cursor = 0;
  let transformed = '';
  const matched: NamedPromptMaterialMatch[] = [];
  for (const candidate of selected) {
    transformed += text.slice(cursor, candidate.start);
    transformed += `@${candidate.name}`;
    cursor = candidate.end;
    if (!matched.some((item) => item.nodeId === candidate.node.id)) {
      matched.push({ nodeId: candidate.node.id, name: candidate.name });
    }
  }
  transformed += text.slice(cursor);

  return {
    text: transformed,
    matched,
  };
}

/** 提交前将 `@素材名` 映射为现有供应商协议使用的 `@图N/视频N/音频N`。 */
export function replaceNamedPromptMentionsForSubmit(
  text: string,
  nodes: PromptMaterialNode[],
): string {
  const active = nodes.filter((node) => node.connected && isPromptMaterialNode(node));
  const activeByName = new Map<string, typeof active>();
  for (const node of active.filter((candidate) => candidate.name.trim())) {
    const key = promptMaterialNameKey(node.name);
    const group = activeByName.get(key) || [];
    group.push(node);
    activeByName.set(key, group);
  }
  const names = Array.from(activeByName.values())
    .map((candidates) => candidates[0])
    .sort((a, b) => a.name.trim().length - b.name.trim().length);
  if (!names.length) return text;

  let imageIdx = 0;
  let videoIdx = 0;
  let audioIdx = 0;
  const labels = new Map<string, string>();
  for (const node of active) {
    const type = node.type as PromptMentionKind;
    const index = type === 'image' ? ++imageIdx : type === 'video' ? ++videoIdx : ++audioIdx;
    const key = `${type}:${promptMaterialNameKey(node.name)}`;
    if (!labels.has(key)) labels.set(key, buildPromptMentionLabel(type, index));
  }

  const pattern = names.map((node) => escapeRegExp(node.name.trim())).join('|');
  return text.replace(new RegExp(`@(${pattern})`, 'giu'), (full, rawName: string) => {
    const node = names.find(
      (candidate) => promptMaterialNameKey(candidate.name) === promptMaterialNameKey(rawName),
    );
    if (!node) return full;
    const label = labels.get(`${node.type}:${promptMaterialNameKey(node.name)}`);
    return label ? `@${label}` : full;
  });
}

/** 取消激活或删除素材槽时，将对应的 `@素材名` 恢复为普通文本。 */
export function removeNamedPromptMention(text: string, name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return text;
  return text.split(`@${trimmed}`).join(trimmed);
}

export type PromptMentionKind = 'image' | 'video' | 'audio';

export interface PromptMentionRef {
  kind: PromptMentionKind;
  num: number;
}

const isMentionableNodeType = (type: string): type is PromptMentionKind =>
  type === 'image' || type === 'video' || type === 'audio';

/** 已激活素材在 nodes 顺序中的 @ 序号（图片、视频、音频各自从 1 编号）。 */
export function getConnectedMentionIndexForNode(
  nodes: Array<{ id: string; type: string; connected: boolean }>,
  nodeId: string,
): PromptMentionRef | null {
  const target = nodes.find((n) => n.id === nodeId);
  if (!target?.connected || !isMentionableNodeType(target.type)) return null;

  let num = 0;
  for (const n of nodes) {
    if (n.type !== target.type || !n.connected) continue;
    num += 1;
    if (n.id === nodeId) {
      return { kind: target.type, num };
    }
  }
  return null;
}

/** 素材槽即将激活时，其在同类型已激活列表中的 @ 序号（激活后占用此序号）。 */
export function getMentionIndexOnConnect(
  nodes: Array<{ id: string; type: string; connected: boolean }>,
  nodeId: string,
): PromptMentionRef | null {
  const target = nodes.find((n) => n.id === nodeId);
  if (!target || target.connected || !isMentionableNodeType(target.type)) return null;

  let num = 0;
  for (const n of nodes) {
    if (n.id === nodeId) {
      return { kind: target.type, num: num + 1 };
    }
    if (n.type === target.type && n.connected) {
      num += 1;
    }
  }
  return null;
}

function normalizePromptTextAfterMentionEdit(text: string): string {
  return text
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();
}

function applyMentionNumberRemap(
  text: string,
  kind: PromptMentionKind,
  remap: (num: number) => number | null,
): string {
  if (!text) return text;

  const replaceAt = (input: string, tokenRe: RegExp, build: (n: number) => string) =>
    input.replace(tokenRe, (match, numStr) => {
      const raw = parseInt(numStr, 10);
      const next = remap(raw);
      if (next === null) return '';
      return next === raw ? match : build(next);
    });

  let result = text;
  if (kind === 'video') {
    result = replaceAt(result, /@视频(\d+)/g, (n) => `@视频${n}`);
    result = replaceAt(result, /(?<!@)视频(\d+)/g, (n) => `视频${n}`);
  } else if (kind === 'audio') {
    result = replaceAt(result, /@音频(\d+)/g, (n) => `@音频${n}`);
    result = replaceAt(result, /(?<!@)音频(\d+)/g, (n) => `音频${n}`);
  } else {
    result = replaceAt(result, /@图(\d+)/g, (n) => `@图${n}`);
    result = replaceAt(result, /(?<!片)(?<!@)图(\d+)/g, (n) => `图${n}`);
  }

  return normalizePromptTextAfterMentionEdit(result);
}

/**
 * 取消激活或删除某个素材槽后，同步提示词中的 @图N / @视频N / @音频N（及裸引用）：
 * 删除对应序号引用，并将其后同类型序号全部减 1。
 */
export function adjustPromptMentionsAfterSlotRemoved(
  text: string,
  kind: PromptMentionKind,
  removedNum: number,
): string {
  if (!text || removedNum < 1) return text;

  return applyMentionNumberRemap(text, kind, (num) => {
    if (num === removedNum) return null;
    if (num > removedNum) return num - 1;
    return num;
  });
}

/**
 * 激活某个素材槽后，将其后同类型的 @引用序号全部加 1，
 * 为新激活槽腾出对应序号。
 */
export function adjustPromptMentionsAfterSlotInserted(
  text: string,
  kind: PromptMentionKind,
  insertAt: number,
): string {
  if (!text || insertAt < 1) return text;

  return applyMentionNumberRemap(text, kind, (num) => (num >= insertAt ? num + 1 : num));
}

/** 批量更新多段提示词（工作台 / 重新生成弹窗共用）。 */
export function adjustPromptItemsAfterSlotRemoved<T extends { text: string }>(
  items: T[],
  kind: PromptMentionKind,
  removedNum: number,
): T[] {
  if (removedNum < 1) return items;
  return items.map((p) => ({
    ...p,
    text: adjustPromptMentionsAfterSlotRemoved(p.text, kind, removedNum),
  }));
}

export function adjustPromptItemsAfterSlotInserted<T extends { text: string }>(
  items: T[],
  kind: PromptMentionKind,
  insertAt: number,
): T[] {
  if (insertAt < 1) return items;
  return items.map((p) => ({
    ...p,
    text: adjustPromptMentionsAfterSlotInserted(p.text, kind, insertAt),
  }));
}

/** 与 textarea 布局一致的隐藏镜像，用于把指针映射到字符索引（支持自动换行）。 */
export const mountPromptMeasureMirror = (textarea: HTMLTextAreaElement): HTMLDivElement => {
  const mirror = document.createElement('div');
  const cs = getComputedStyle(textarea);
  mirror.style.position = 'fixed';
  mirror.style.left = '-99999px';
  mirror.style.top = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflowWrap = 'break-word';
  mirror.style.width = `${textarea.clientWidth}px`;
  mirror.style.boxSizing = cs.boxSizing;
  mirror.style.padding = cs.padding;
  mirror.style.border = cs.border;
  mirror.style.font = cs.font;
  mirror.style.lineHeight = cs.lineHeight;
  mirror.style.letterSpacing = cs.letterSpacing;
  mirror.style.textIndent = cs.textIndent;
  mirror.style.textTransform = cs.textTransform;
  mirror.textContent = textarea.value;
  document.body.appendChild(mirror);
  return mirror;
};

/** 指针相对于 textarea 内容区的坐标（含滚动）。 */
export const promptPointerContentOffset = (
  textarea: HTMLTextAreaElement,
  clientX: number,
  clientY: number,
) => {
  const taRect = textarea.getBoundingClientRect();
  const cs = getComputedStyle(textarea);
  const padTop = parseFloat(cs.paddingTop) || 0;
  const padLeft = parseFloat(cs.paddingLeft) || 0;
  const borderTop = parseFloat(cs.borderTopWidth) || 0;
  const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
  const innerLeft = taRect.left + borderLeft + padLeft;
  const innerTop = taRect.top + borderTop + padTop;
  return {
    x: clientX - innerLeft + textarea.scrollLeft,
    y: clientY - innerTop + textarea.scrollTop,
  };
};

/** 将指针映射到最接近的 caret 索引（用于识别悬停的 @图N / @视频N / @音频N）。 */
export const getTextareaIndexNearPoint = (
  textarea: HTMLTextAreaElement,
  clientX: number,
  clientY: number,
): number => {
  const text = textarea.value;
  if (!text.length) return 0;
  const { x: relX, y: relY } = promptPointerContentOffset(textarea, clientX, clientY);
  const mirror = mountPromptMeasureMirror(textarea);
  const tn = mirror.firstChild as Text;
  if (!tn) {
    mirror.remove();
    return 0;
  }
  const mr = mirror.getBoundingClientRect();
  // relX/relY 以 textarea「内容区」原点（已扣除 padding/border）为基准；
  // 因此镜像里的 caret 矩形也必须换算到镜像内容区原点，否则会有恒定 padding 偏移。
  const mcs = getComputedStyle(mirror);
  const mContentLeft =
    mr.left + (parseFloat(mcs.borderLeftWidth) || 0) + (parseFloat(mcs.paddingLeft) || 0);
  const mContentTop =
    mr.top + (parseFloat(mcs.borderTopWidth) || 0) + (parseFloat(mcs.paddingTop) || 0);
  let bestIdx = 0;
  let bestDist = Infinity;
  const len = text.length;
  for (let i = 0; i <= len; i++) {
    const range = document.createRange();
    range.setStart(tn, i);
    range.setEnd(tn, i);
    const rects = range.getClientRects();
    const boxes: DOMRect[] =
      rects.length > 0
        ? Array.from(rects)
        : (() => {
            const b = range.getBoundingClientRect();
            return b.width === 0 && b.height === 0 ? [] : [b];
          })();
    for (let r = 0; r < boxes.length; r++) {
      const box = boxes[r];
      const rl = box.left - mContentLeft;
      const rt = box.top - mContentTop;
      const rr = box.right - mContentLeft;
      const rb = box.bottom - mContentTop;
      const px = Math.min(Math.max(relX, rl), rr);
      const py = Math.min(Math.max(relY, rt), rb);
      const d = (relX - px) ** 2 + (relY - py) ** 2;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
  }
  mirror.remove();
  return bestIdx;
};

/**
 * 与提示词展示层一致的素材引用片段（含光标悬停预览）：
 * - @图N / @视频N / @音频N（编辑态）
 * - 图N / 视频N / 音频N（提交/API 常用形态）；图N 排除「图片」中的「图」
 */
export function splitPromptTextForHighlight(
  text: string,
  namedMentions: string[] = [],
): string[] {
  const namedPatterns = Array.from(
    new Set(
      namedMentions
        .map((name) => name.trim().replace(/^@+/, ''))
        .filter(Boolean),
    ),
  )
    .sort((a, b) => a.length - b.length)
    .map((name) => `@${escapeRegExp(name)}`);
  const mediaPatterns = [
    '@(?:图|视频|音频)\\d+',
    ...namedPatterns,
    '视频\\d+',
    '音频\\d+',
    '(?<!片)图\\d+',
  ];
  return text.split(new RegExp(`(${mediaPatterns.join('|')})`, 'giu'));
}

export interface NamedPromptMentionRange {
  name: string;
  start: number;
  end: number;
}

/** 返回点击位置所在的命名素材引用及其文本范围；命中范围不包含后续普通文本。 */
export const getNamedPromptMentionRangeAtIndex = (
  text: string,
  index: number,
  namedMentions: string[] = [],
): NamedPromptMentionRange | null => {
  const names = Array.from(
    new Set(
      namedMentions
        .map((name) => name.trim().replace(/^@+/, ''))
        .filter(Boolean),
    ),
  ).sort((a, b) => b.length - a.length);

  for (const name of names) {
    const mentionRe = new RegExp(`@${escapeRegExp(name)}`, 'giu');
    let match: RegExpExecArray | null;
    while ((match = mentionRe.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      if (index >= start && index < end) return { name, start, end };
    }
  }
  return null;
};

/** 返回点击位置所在的命名素材引用。 */
export const getNamedPromptMentionAtIndex = (
  text: string,
  index: number,
  namedMentions: string[] = [],
): string | null => getNamedPromptMentionRangeAtIndex(text, index, namedMentions)?.name ?? null;

/** 仅解除点击位置这一处命名素材引用，不改变素材的激活状态。 */
export const removeNamedPromptMentionAtIndex = (
  text: string,
  index: number,
  namedMentions: string[] = [],
): string => {
  const range = getNamedPromptMentionRangeAtIndex(text, index, namedMentions);
  if (!range) return text;
  return `${text.slice(0, range.start)}${text.slice(range.start + 1, range.end)}${text.slice(range.end)}`;
};

export function promptTextHasMediaRefSegments(text: string): boolean {
  const re = /@(?:图|视频|音频)\d+|视频\d+|音频\d+|(?<!片)图\d+/;
  return re.test(text);
}

/** 若 index 落在 @图N、@视频N、@音频N 或裸引用片段内，返回类型与序号。 */
export const getPromptMentionAtIndex = (text: string, index: number): PromptMentionRef | null => {
  const re = /@(?:图|视频|音频)(\d+)|视频(\d+)|音频(\d+)|(?<!片)图(\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const token = m[0];
    const end = start + token.length;
    if (index < start || index >= end) continue;
    if (token.startsWith('@')) {
      const num = parseInt(m[1]!, 10);
      if (token.startsWith('@视频')) return { kind: 'video', num };
      if (token.startsWith('@音频')) return { kind: 'audio', num };
      return { kind: 'image', num };
    }
    if (token.startsWith('视频')) {
      return { kind: 'video', num: parseInt(m[2]!, 10) };
    }
    if (token.startsWith('音频')) {
      return { kind: 'audio', num: parseInt(m[3]!, 10) };
    }
    return { kind: 'image', num: parseInt(m[4]!, 10) };
  }
  return null;
};

/** @deprecated 使用 {@link getPromptMentionAtIndex} */
export const getFigureMentionNumberAtIndex = (text: string, index: number): number | null =>
  getPromptMentionAtIndex(text, index)?.num ?? null;

/** 已激活素材的 @ 候选项：图片、视频、音频各自从 1 编号。 */
export const buildPromptMentionLabel = (
  type: PromptMentionKind,
  indexWithinType: number,
): string => {
  if (type === 'video') return `视频${indexWithinType}`;
  if (type === 'audio') return `音频${indexWithinType}`;
  return `图${indexWithinType}`;
};

export const findPromptMentionCandidate = <
  T extends { label: string },
>(
  candidates: T[],
  ref: PromptMentionRef,
): T | undefined => {
  const label = buildPromptMentionLabel(ref.kind, ref.num);
  return candidates.find((c) => c.label === label);
};
